import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import api from '../../services/api';
import { useGpsEntregador } from './hooks/useGpsEntregador';
import 'leaflet/dist/leaflet.css';

// --- CONFIGURAÇÕES DE ÁUDIO ---
const SOM_NOTIFICACAO = "https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3"; // Som de "pop/ding"

const AutoCenter = ({ coords, status }) => {
    const map = useMap();
    useEffect(() => { 
        if (coords && status === 'saiu_para_entrega') {
            map.setView(coords, 16); 
        }
    }, [coords, status, map]);
    return null;
};

const iconL = (emoji) => L.divIcon({
    html: `<div style="font-size:32px; filter:drop-shadow(0 4px 8px rgba(0,0,0,0.25))">${emoji}</div>`,
    className: 'custom-marker', iconSize: [40, 40]
});

const AcompanharPedido = () => {
    const { pedidoId } = useParams();
    const [pedido, setPedido] = useState(null);
    const [itens, setItens] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const audioRef = useRef(new Audio(SOM_NOTIFICACAO));

    const { posicaoMoto, socket } = useGpsEntregador(null, null, null, pedidoId);

    const statusMap = {
        'pendente': { label: 'Confirmando pedido', step: 1, color: '#f5a623' },
        'preparando': { label: 'Preparando seu pedido', step: 2, color: '#f5a623' },
        'saiu_para_entrega': { label: 'A caminho de você', step: 3, color: '#ea1d2c' },
        'concluido': { label: 'Pedido entregue', step: 4, color: '#00a082' }
    };

    const currentInfo = statusMap[pedido?.pedido_status] || statusMap['pendente'];

    // Função para tocar som e disparar notificação
    const dispararNotificacao = (novoStatus) => {
        audioRef.current.play().catch(e => console.log("Áudio bloqueado pelo navegador"));
        
        if (Notification.permission === "granted") {
            new Notification("Atualização do Pedido", {
                body: `Seu pedido agora está: ${statusMap[novoStatus]?.label || novoStatus}`,
                icon: "/logo192.png" 
            });
        }
    };

    useEffect(() => {
        // Pedir permissão para notificações
        if (Notification.permission !== "granted") {
            Notification.requestPermission();
        }

        const loadData = async () => {
            try {
                const res = await api.get(`/pedidos/detalhes/${pedidoId}`);
                if (res.data.success) {
                    setPedido(res.data.pedido);
                    setItens(res.data.itens || []);
                }
            } catch (err) { console.error(err); }
            finally { setCarregando(false); }
        };
        loadData();
    }, [pedidoId]);

    useEffect(() => {
        if (socket) {
            socket.on('status-changed', (data) => {
                if (data.pedidoId === parseInt(pedidoId)) {
                    setPedido(prev => ({ ...prev, pedido_status: data.novoStatus }));
                    dispararNotificacao(data.novoStatus);
                }
            });
        }
    }, [socket, pedidoId]);

    if (carregando) return <div style={styles.loading}>Aguarde, estamos localizando seu pedido...</div>;
    if (!pedido) return <div style={styles.loading}>Pedido não encontrado.</div>;

    const coordsLoja = [parseFloat(pedido.lat_loja), parseFloat(pedido.lng_loja)];
    const coordsCliente = [parseFloat(pedido.lat_cliente), parseFloat(pedido.lng_cliente)];

    return (
        <div style={styles.container}>
            {/* MAPA */}
            <div style={styles.mapWrapper}>
                <MapContainer center={coordsLoja} zoom={15} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                    <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
                    <AutoCenter coords={posicaoMoto} status={pedido.pedido_status} />

                    <Marker position={coordsLoja} icon={iconL('🏪')} />
                    <Marker position={coordsCliente} icon={iconL('📍')} />
                    {posicaoMoto && <Marker position={posicaoMoto} icon={iconL('🛵')} />}

                    {coordsLoja && coordsCliente && (
                        <Polyline positions={[coordsLoja, coordsCliente]} color="#ea1d2c" dashArray="10, 10" weight={2} opacity={0.5} />
                    )}
                </MapContainer>

                {/* BOTÃO VOLTAR (FLUTUANTE) */}
                <button onClick={() => window.history.back()} style={styles.backButton}>←</button>
            </div>

            {/* INFO CARD (ESTILO IFOOD) */}
            <div style={styles.infoCard}>
                <div style={styles.dragHandle} />
                
                <header style={styles.header}>
                    <div>
                        <h1 style={styles.statusTitle}>{currentInfo.label}</h1>
                        <p style={styles.storeName}>Pedido em {pedido.loja_nome}</p>
                    </div>
                    <img src={pedido.loja_logo || 'https://via.placeholder.com/50'} style={styles.logo} alt="loja" />
                </header>

                {/* PROGRESS BAR */}
                <div style={styles.progressContainer}>
                    {[1, 2, 3, 4].map(step => (
                        <div key={step} style={{
                            ...styles.progressBar,
                            backgroundColor: currentInfo.step >= step ? currentInfo.color : '#eee'
                        }} />
                    ))}
                </div>

                <div style={styles.deliveryInfo}>
                    <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Previsão de entrega</span>
                        <span style={styles.infoValue}>25-35 min</span>
                    </div>
                    <div style={styles.divider} />
                    <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Pagamento</span>
                        <span style={styles.infoValue}>{pedido.tipo_pagamento}</span>
                    </div>
                </div>

                <div style={styles.addressBox}>
                    <span style={{marginRight: 8}}>🏠</span>
                    <span style={styles.addressText}>{pedido.endereco_entrega}</span>
                </div>

                <details style={styles.details}>
                    <summary style={styles.summary}>Ver detalhes do pedido</summary>
                    <div style={styles.itensList}>
                        {itens.map(it => (
                            <div key={it.id} style={styles.item}>
                                <span>{it.quantidade}x {it.nome}</span>
                                <span>R$ {it.preco_unitario}</span>
                            </div>
                        ))}
                        <div style={styles.totalRow}>
                            <strong>Total</strong>
                            <strong>R$ {pedido.total}</strong>
                        </div>
                    </div>
                </details>
            </div>
        </div>
    );
};

// --- ESTILOS ---
const styles = {
    container: { height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#fff', overflow: 'hidden' },
    loading: { padding: '100px 20px', textAlign: 'center', fontSize: '18px', color: '#666' },
    mapWrapper: { height: '50%', width: '100%', position: 'relative' },
    backButton: { 
        position: 'absolute', top: 20, left: 20, zIndex: 1000, border: 'none', 
        backgroundColor: '#fff', width: 40, height: 40, borderRadius: '50%', 
        boxShadow: '0 2px 10px rgba(0,0,0,0.2)', cursor: 'pointer', fontSize: '20px' 
    },
    infoCard: { 
        flex: 1, backgroundColor: '#fff', borderTopLeftRadius: '24px', borderTopRightRadius: '24px', 
        marginTop: '-24px', zIndex: 1001, padding: '0 20px 20px', boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
        display: 'flex', flexDirection: 'column'
    },
    dragHandle: { width: 40, height: 5, backgroundColor: '#e2e2e2', borderRadius: 10, margin: '12px auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
    statusTitle: { fontSize: '1.25rem', fontWeight: 'bold', margin: 0, color: '#3e3e3e' },
    storeName: { fontSize: '0.9rem', color: '#717171', margin: '4px 0 0' },
    logo: { width: 48, height: 48, borderRadius: '50%', border: '1px solid #eee' },
    progressContainer: { display: 'flex', gap: 6, marginBottom: 24 },
    progressBar: { height: 4, flex: 1, borderRadius: 2 },
    deliveryInfo: { 
        display: 'flex', backgroundColor: '#f9f9f9', borderRadius: '12px', padding: '15px', 
        justifyContent: 'space-around', marginBottom: 20 
    },
    infoItem: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
    infoLabel: { fontSize: '0.7rem', color: '#717171', textTransform: 'uppercase', marginBottom: 4 },
    infoValue: { fontSize: '0.9rem', fontWeight: 'bold', color: '#3e3e3e' },
    divider: { width: 1, backgroundColor: '#e2e2e2' },
    addressBox: { display: 'flex', alignItems: 'center', padding: '0 5px', marginBottom: 20 },
    addressText: { fontSize: '0.85rem', color: '#3e3e3e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    details: { borderTop: '1px solid #f2f2f2', paddingTop: 15 },
    summary: { fontSize: '0.9rem', color: '#ea1d2c', fontWeight: 'bold', cursor: 'pointer', outline: 'none' },
    itensList: { padding: '15px 0' },
    item: { display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#717171', marginBottom: 8 },
    totalRow: { display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #eee', paddingTop: 10, marginTop: 10, color: '#3e3e3e' }
};

export default AcompanharPedido;